import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Message } from "@cloudflare/realtimekit";
import { cn } from "@/lib/utils";
import { getLivestreamChatText } from "./useLivestreamChat";
import { LivestreamPinnedCommentBubble } from "./LivestreamPinnedCommentOverlay";
import type { LivestreamPinnedComment } from "./livestreamPinnedComment";

const OVERLAY_DURATION_MS = 7000;
const HISTORY_IDLE_TIMEOUT_MS = 5000;
const HISTORY_FADE_OUT_MS = 500;
const MAX_OVERLAY_MESSAGES = 5000;

type OverlayItem = {
  id: string;
  displayName: string;
  text: string;
  expired: boolean;
};

type HistoryPhase = "closed" | "open" | "closing";

export function LivestreamChatOverlay({
  messages,
  pinnedComment,
  className,
}: {
  messages: Message[];
  pinnedComment?: LivestreamPinnedComment | null;
  className?: string;
}) {
  const [overlayItems, setOverlayItems] = useState<OverlayItem[]>([]);
  const [historyPhase, setHistoryPhase] = useState<HistoryPhase>("closed");
  const seenIdsRef = useRef(new Set<string>());
  const scrollRef = useRef<HTMLDivElement>(null);
  const historyPhaseRef = useRef<HistoryPhase>("closed");
  const isInitialSyncRef = useRef(true);
  const expireTimeoutIdsRef = useRef(new Map<string, number>());
  const idleTimeoutIdRef = useRef<number | null>(null);
  const fadeOutTimeoutIdRef = useRef<number | null>(null);

  historyPhaseRef.current = historyPhase;
  const historyVisible = historyPhase !== "closed";
  const historyOpen = historyPhase === "open";

  useEffect(() => {
    return () => {
      for (const timeoutId of expireTimeoutIdsRef.current.values()) {
        window.clearTimeout(timeoutId);
      }
      expireTimeoutIdsRef.current.clear();
      if (idleTimeoutIdRef.current != null) {
        window.clearTimeout(idleTimeoutIdRef.current);
      }
      if (fadeOutTimeoutIdRef.current != null) {
        window.clearTimeout(fadeOutTimeoutIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const nextItems: OverlayItem[] = [];
    const treatAsHistorical = isInitialSyncRef.current;

    for (const message of messages) {
      if (seenIdsRef.current.has(message.id)) continue;
      const text = getLivestreamChatText(message);
      if (!text) continue;

      seenIdsRef.current.add(message.id);
      nextItems.push({
        id: message.id,
        displayName: message.displayName,
        text,
        expired: treatAsHistorical,
      });
    }

    if (nextItems.length === 0) return;
    isInitialSyncRef.current = false;

    for (const item of nextItems) {
      if (item.expired) continue;
      const timeoutId = window.setTimeout(() => {
        expireTimeoutIdsRef.current.delete(item.id);
        setOverlayItems((prev) =>
          prev.map((entry) =>
            entry.id === item.id ? { ...entry, expired: true } : entry,
          ),
        );
      }, OVERLAY_DURATION_MS);
      expireTimeoutIdsRef.current.set(item.id, timeoutId);
    }

    setOverlayItems((prev) => [...prev, ...nextItems].slice(-MAX_OVERLAY_MESSAGES));
  }, [messages]);

  const displayedItems = historyVisible
    ? overlayItems
    : overlayItems.filter((item) => !item.expired);
  const hasScrollback = overlayItems.some((item) => item.expired);

  const clearIdleTimeout = () => {
    if (idleTimeoutIdRef.current == null) return;
    window.clearTimeout(idleTimeoutIdRef.current);
    idleTimeoutIdRef.current = null;
  };

  const clearFadeOutTimeout = () => {
    if (fadeOutTimeoutIdRef.current == null) return;
    window.clearTimeout(fadeOutTimeoutIdRef.current);
    fadeOutTimeoutIdRef.current = null;
  };

  const beginHistoryClose = () => {
    clearIdleTimeout();
    if (historyPhaseRef.current !== "open") return;
    setHistoryPhase("closing");
  };

  const resetIdleTimeout = () => {
    if (historyPhaseRef.current !== "open") return;
    clearIdleTimeout();
    idleTimeoutIdRef.current = window.setTimeout(() => {
      idleTimeoutIdRef.current = null;
      beginHistoryClose();
    }, HISTORY_IDLE_TIMEOUT_MS);
  };

  const enterHistoryMode = () => {
    if (overlayItems.length === 0) return;
    clearFadeOutTimeout();
    if (historyPhaseRef.current === "open") {
      resetIdleTimeout();
      return;
    }
    setHistoryPhase("open");
  };

  useEffect(() => {
    if (historyPhase !== "open") {
      clearIdleTimeout();
      return;
    }

    clearIdleTimeout();
    idleTimeoutIdRef.current = window.setTimeout(() => {
      idleTimeoutIdRef.current = null;
      beginHistoryClose();
    }, HISTORY_IDLE_TIMEOUT_MS);

    return () => {
      clearIdleTimeout();
    };
  }, [historyPhase]);

  useEffect(() => {
    if (historyPhase !== "closing") {
      clearFadeOutTimeout();
      return;
    }

    clearFadeOutTimeout();
    fadeOutTimeoutIdRef.current = window.setTimeout(() => {
      fadeOutTimeoutIdRef.current = null;
      setHistoryPhase("closed");
    }, HISTORY_FADE_OUT_MS);

    return () => {
      clearFadeOutTimeout();
    };
  }, [historyPhase]);

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl || historyVisible) return;
    scrollEl.scrollTop = scrollEl.scrollHeight;
  }, [displayedItems, historyVisible]);

  useLayoutEffect(() => {
    if (!historyOpen) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    scrollEl.scrollTop = scrollEl.scrollHeight;
  }, [historyOpen]);

  const handleActivity = () => {
    if (historyPhaseRef.current === "closing") {
      enterHistoryMode();
      return;
    }
    resetIdleTimeout();
  };

  const handlePointerDown = () => {
    enterHistoryMode();
  };

  const showPinnedComment = Boolean(pinnedComment?.text);
  if (overlayItems.length === 0 && !showPinnedComment) return null;

  const showScrollbackHitTarget =
    !historyVisible && displayedItems.length === 0 && hasScrollback;

  return (
    <div
      className={cn(
        "absolute bottom-20 left-3 right-16 z-40 flex max-h-[45%] flex-col justify-end gap-1.5 sm:left-4 sm:bottom-24",
        className,
      )}
      aria-live="polite"
    >
      {showScrollbackHitTarget ? (
        <div
          className="pointer-events-auto h-16 w-[85%] max-w-sm"
          onPointerDown={handlePointerDown}
          aria-label="Show chat history"
        />
      ) : (
        <div
          ref={scrollRef}
          onPointerDown={handlePointerDown}
          onScroll={handleActivity}
          onWheel={handleActivity}
          onPointerMove={handleActivity}
          onTouchMove={handleActivity}
          className={cn(
            "flex min-h-0 w-full flex-col gap-1.5 overflow-y-auto overscroll-contain pointer-events-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            historyVisible ? "max-h-[45vh]" : "max-h-full",
          )}
        >
          {displayedItems.map((item) => {
            const isHistoryMessage = historyVisible && item.expired;
            const isLiveInHistory = historyVisible && !item.expired;

            return (
              <div
                key={item.id}
                className={cn(
                  "max-w-[85%] shrink-0 self-start rounded-2xl bg-black/35 px-3 py-1.5 text-sm text-white shadow-[0_1px_8px_rgba(0,0,0,0.45)] backdrop-blur-sm",
                  isHistoryMessage && "transition-opacity ease-out",
                  isHistoryMessage && historyOpen && "opacity-100",
                  isHistoryMessage && historyPhase === "closing" && "opacity-0",
                  isLiveInHistory && historyOpen && "opacity-100",
                  isLiveInHistory && historyPhase === "closing" && "livestream-chat-bubble",
                  !historyVisible && "livestream-chat-bubble",
                )}
                style={
                  isHistoryMessage
                    ? { transitionDuration: `${HISTORY_FADE_OUT_MS}ms` }
                    : undefined
                }
              >
                <span className="font-semibold">{item.displayName}</span>
                <span className="opacity-90"> {item.text}</span>
              </div>
            );
          })}
        </div>
      )}
      {showPinnedComment && pinnedComment ? (
        <LivestreamPinnedCommentBubble
          pinnedComment={pinnedComment}
          className="pointer-events-none shrink-0"
        />
      ) : null}
      <style>{`
        @keyframes livestream-chat-fade {
          0%, 55% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .livestream-chat-bubble {
          animation: livestream-chat-fade ${OVERLAY_DURATION_MS}ms ease-out forwards;
        }
      `}</style>
    </div>
  );
}
